// Shared video-upload helpers for the candidate flows (mobile token page + the
// desktop /crr recorder). Supports two modes, chosen by the server's
// /api/video-resume/upload-config flag:
//
//   • direct  — browser uploads STRAIGHT to S3 via a presigned POST (no size
//               limit through the app server; handles 200MB+ iPhone videos),
//               then calls finalize so the server registers it + queues the
//               AI review.
//   • server  — legacy path: POST the file to the app server (≤90MB).
//
// Both report progress and return { review_pending, prev_analyzed_at } so the
// caller can poll for the async AI review.

let _cfgCache = null;

export async function getUploadConfig() {
  if (_cfgCache) return _cfgCache;
  try {
    const r = await fetch("/api/video-resume/upload-config", { credentials: "include" });
    const j = await r.json();
    _cfgCache = {
      directUpload: !!j.directUpload,
      maxDirectBytes: j.maxDirectBytes || 500 * 1024 * 1024,
      maxServerBytes: j.maxServerBytes || 90 * 1024 * 1024,
    };
  } catch {
    _cfgCache = { directUpload: false, maxDirectBytes: 500 * 1024 * 1024, maxServerBytes: 90 * 1024 * 1024 };
  }
  return _cfgCache;
}

// POST a FormData with real upload progress. Resolves with the XHR.
function xhrPost(url, formData, { onProgress, timeoutMs = 30 * 60 * 1000 } = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url);
    xhr.timeout = timeoutMs;
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      };
    }
    xhr.onload = () => resolve(xhr);
    xhr.onerror = () => reject(new Error("network"));
    xhr.ontimeout = () => reject(new Error("timeout"));
    xhr.send(formData);
  });
}

// Direct-to-S3: presign → PUT-via-POST to S3 (with progress) → finalize.
// endpoints = { presign, finalize }. Returns finalize JSON.
export async function uploadVideoDirect({ endpoints, file, durationSeconds, onProgress }) {
  // 1) ask the server for a presigned POST
  const pr = await fetch(endpoints.presign, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ content_type: file.type || "video/mp4" }),
  });
  const pj = await pr.json();
  if (!pr.ok || !pj.ok) {
    const err = new Error(pj.error || "Could not prepare the upload.");
    err.status = pr.status;
    throw err;
  }

  // 2) upload the file straight to S3 (fields first, file LAST — S3 requires it)
  const form = new FormData();
  Object.entries(pj.fields || {}).forEach(([k, v]) => form.append(k, v));
  form.append("file", file);
  const s3xhr = await xhrPost(pj.url, form, { onProgress });
  if (s3xhr.status < 200 || s3xhr.status >= 300) {
    // 403 with EntityTooLarge = over the S3 size condition
    const tooBig = /EntityTooLarge/i.test(s3xhr.responseText || "");
    throw new Error(tooBig
      ? "That video is too large — record a shorter clip or lower your camera quality."
      : "Upload to storage failed — please try again.");
  }

  // 3) finalize: server registers the video + queues the AI review
  const fr = await fetch(endpoints.finalize, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ key: pj.key, duration_seconds: durationSeconds ?? null }),
  });
  const fj = await fr.json();
  if (!fr.ok || !fj.ok) {
    const err = new Error(fj.error || "Could not save the upload.");
    err.status = fr.status;
    throw err;
  }
  return fj; // { ok, filename, review_pending, prev_analyzed_at }
}

// Legacy server upload: POST the file to the app server (with progress).
export async function uploadVideoToServer({ url, file, durationSeconds, onProgress }) {
  const form = new FormData();
  form.append("video", file);
  if (durationSeconds != null) form.append("duration_seconds", String(durationSeconds));
  const xhr = await xhrPost(url, form, { onProgress, timeoutMs: 3 * 60 * 1000 });
  let json = {};
  try { json = JSON.parse(xhr.responseText || "{}"); } catch { /* non-JSON */ }
  if (xhr.status < 200 || xhr.status >= 300 || !json.ok) {
    const err = new Error(json.error || "Upload failed. Please try again.");
    err.status = xhr.status;
    throw err;
  }
  return json;
}
